import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { useAuthStore } from '@/stores/authStore'
import type { DashboardUser } from '@alertwhale/shared'

interface LoginResponse {
  user: DashboardUser
  token: string
  tenantId: string
  requires2FA?: boolean
  tempToken?: string
}

interface RegisterResponse {
  user: DashboardUser
  token: string
  tenantId: string
}

// API response uses snake_case
interface AuthConfigResponse {
  recaptcha: {
    enabled: boolean
    site_key: string | null
  }
  google_oauth: {
    enabled: boolean
    client_id: string | null
  }
  github_oauth?: {
    enabled: boolean
    client_id: string | null
  }
  email_otp: {
    enabled: boolean
  }
  two_factor: {
    enabled: boolean
  }
}

// Transformed to camelCase for frontend use
interface AuthConfig {
  recaptcha: {
    enabled: boolean
    siteKey: string | null
  }
  googleOAuth: {
    enabled: boolean
    clientId: string | null
  }
  githubOAuth: {
    enabled: boolean
    clientId: string | null
  }
  emailOTP: {
    enabled: boolean
  }
  twoFactorAuth: {
    enabled: boolean
  }
}

interface OTPResponse {
  message: string
  isNewUser: boolean
}

interface TwoFASetupResponse {
  secret: string
  qrCodeUri: string
  backupCodes: string[]
}

interface TwoFAStatusResponse {
  enabled: boolean
  hasBackupCodes: boolean
}

export function useAuthConfig() {
  return useQuery({
    queryKey: ['authConfig'],
    queryFn: async (): Promise<AuthConfig> => {
      const response = await api.get<AuthConfigResponse>('/auth/config')
      // Transform snake_case to camelCase
      return {
        recaptcha: {
          enabled: response.recaptcha.enabled,
          siteKey: response.recaptcha.site_key,
        },
        googleOAuth: {
          enabled: response.google_oauth.enabled,
          clientId: response.google_oauth.client_id,
        },
        githubOAuth: {
          enabled: response.github_oauth?.enabled ?? false,
          clientId: response.github_oauth?.client_id ?? null,
        },
        emailOTP: {
          enabled: response.email_otp.enabled,
        },
        twoFactorAuth: {
          enabled: response.two_factor.enabled,
        },
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

export function useLogin() {
  const setAuth = useAuthStore((state) => state.setAuth)
  const setTempAuth = useAuthStore((state) => state.setTempAuth)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: { email: string; password: string; recaptchaToken?: string }) => {
      return api.post<LoginResponse>('/auth/login', {
        email: data.email,
        password: data.password,
        recaptcha_token: data.recaptchaToken,
      })
    },
    onSuccess: (data) => {
      if (data.requires2FA && data.tempToken) {
        // Store temp token for 2FA flow
        setTempAuth(data.tempToken, data.user)
      } else {
        setAuth(data.user, data.token, data.tenantId)
        queryClient.invalidateQueries()
      }
    },
  })
}

export function useVerify2FA() {
  const setAuth = useAuthStore((state) => state.setAuth)
  const clearTempAuth = useAuthStore((state) => state.clearTempAuth)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: { token: string; code: string }) => {
      return api.post<LoginResponse>('/auth/login/2fa', {
        temp_token: data.token,
        code: data.code,
      })
    },
    onSuccess: (data) => {
      clearTempAuth()
      setAuth(data.user, data.token, data.tenantId)
      queryClient.invalidateQueries()
    },
  })
}

export function useVerifyBackupCode() {
  const setAuth = useAuthStore((state) => state.setAuth)
  const clearTempAuth = useAuthStore((state) => state.clearTempAuth)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: { token: string; code: string }) => {
      return api.post<LoginResponse>('/auth/login/backup-code', {
        temp_token: data.token,
        code: data.code,
      })
    },
    onSuccess: (data) => {
      clearTempAuth()
      setAuth(data.user, data.token, data.tenantId)
      queryClient.invalidateQueries()
    },
  })
}

export function useRequestOTP() {
  return useMutation({
    mutationFn: async (data: { email: string; recaptchaToken?: string }) => {
      return api.post<OTPResponse>('/auth/otp/request', {
        email: data.email,
        recaptcha_token: data.recaptchaToken,
      })
    },
  })
}

export function useVerifyOTP() {
  const setAuth = useAuthStore((state) => state.setAuth)
  const setTempAuth = useAuthStore((state) => state.setTempAuth)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: { email: string; code: string; name?: string }) => {
      return api.post<LoginResponse>('/auth/otp/verify', data)
    },
    onSuccess: (data) => {
      if (data.requires2FA && data.tempToken) {
        setTempAuth(data.tempToken, data.user)
      } else {
        setAuth(data.user, data.token, data.tenantId)
        queryClient.invalidateQueries()
      }
    },
  })
}

export function useGoogleOAuthCallback() {
  const setAuth = useAuthStore((state) => state.setAuth)
  const setTempAuth = useAuthStore((state) => state.setTempAuth)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: { code: string }) => {
      return api.post<LoginResponse>('/auth/google/callback', data)
    },
    onSuccess: (data) => {
      if (data.requires2FA && data.tempToken) {
        setTempAuth(data.tempToken, data.user)
      } else {
        setAuth(data.user, data.token, data.tenantId)
        queryClient.invalidateQueries()
      }
    },
  })
}

export function useGitHubOAuthCallback() {
  const setAuth = useAuthStore((state) => state.setAuth)
  const setTempAuth = useAuthStore((state) => state.setTempAuth)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: { code: string }) => {
      return api.post<LoginResponse>('/auth/github/callback', data)
    },
    onSuccess: (data) => {
      if (data.requires2FA && data.tempToken) {
        setTempAuth(data.tempToken, data.user)
      } else {
        setAuth(data.user, data.token, data.tenantId)
        queryClient.invalidateQueries()
      }
    },
  })
}

export function useRegister() {
  const setAuth = useAuthStore((state) => state.setAuth)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: {
      email: string
      password: string
      name?: string
      tenantName?: string
      recaptchaToken?: string
    }) => {
      return api.post<RegisterResponse>('/auth/register', {
        email: data.email,
        password: data.password,
        name: data.name,
        tenant_name: data.tenantName,
        recaptcha_token: data.recaptchaToken,
      })
    },
    onSuccess: (data) => {
      setAuth(data.user, data.token, data.tenantId)
      queryClient.invalidateQueries()
    },
  })
}

export function useLogout() {
  const logout = useAuthStore((state) => state.logout)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      await api.post('/auth/logout')
    },
    onSettled: () => {
      logout()
      queryClient.clear()
    },
  })
}

export function useForgotPassword() {
  return useMutation({
    mutationFn: async (data: { email: string; recaptchaToken?: string }) => {
      return api.post('/auth/forgot-password', {
        email: data.email,
        recaptcha_token: data.recaptchaToken,
      })
    },
  })
}

export function useResetPassword() {
  return useMutation({
    mutationFn: async (data: { token: string; password: string }) => {
      return api.post('/auth/reset-password', data)
    },
  })
}

export function useCurrentUser() {
  const token = useAuthStore((state) => state.token)

  return useQuery({
    queryKey: ['currentUser'],
    queryFn: () => api.get<DashboardUser>('/auth/me'),
    enabled: !!token,
  })
}

export function useUpdateProfile() {
  const updateUser = useAuthStore((state) => state.updateUser)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: { name?: string; avatarUrl?: string }) => {
      return api.patch<DashboardUser>('/auth/me', data)
    },
    onSuccess: (data) => {
      updateUser(data)
      queryClient.invalidateQueries({ queryKey: ['currentUser'] })
    },
  })
}

export function useChangePassword() {
  return useMutation({
    mutationFn: async (data: { currentPassword: string; newPassword: string }) => {
      return api.post('/auth/change-password', data)
    },
  })
}

// 2FA Management
export function use2FAStatus() {
  const token = useAuthStore((state) => state.token)

  return useQuery({
    queryKey: ['2faStatus'],
    queryFn: () => api.get<TwoFAStatusResponse>('/auth/2fa/status'),
    enabled: !!token,
  })
}

export function useSetup2FA() {
  return useMutation({
    mutationFn: async () => {
      return api.post<TwoFASetupResponse>('/auth/2fa/setup')
    },
  })
}

export function useEnable2FA() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: { code: string }) => {
      return api.post<{ enabled: boolean; backupCodes: string[] }>('/auth/2fa/enable', data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['2faStatus'] })
    },
  })
}

export function useDisable2FA() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: { code: string }) => {
      return api.post<{ disabled: boolean }>('/auth/2fa/disable', data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['2faStatus'] })
    },
  })
}

export function useRegenerateBackupCodes() {
  return useMutation({
    mutationFn: async (data: { code: string }) => {
      return api.post<{ backupCodes: string[] }>('/auth/2fa/backup-codes/regenerate', data)
    },
  })
}
